import {
  AddEquation,
  Color,
  CustomBlending,
  FrontSide,
  Matrix3,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MirroredRepeatWrapping,
  NormalBlending,
  NotEqualStencilFunc,
  OneFactor,
  ReplaceStencilOp,
  ShaderMaterial,
  SrcAlphaFactor,
  SrcColorFactor,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  ZeroFactor,
  type IUniform,
  type Material,
  type Object3D,
  type Scene,
  type WebGLRenderer,
} from 'three';
import { isSeeThrough, type AcetateSpec } from '../config/acetates';
import { legacyOpacity } from '../config/appearance';

/**
 * A110V — optics of the library acetates (the 36 supplier plates).
 *
 * A see-through acetate is drawn in two passes over the camera image, which is
 * the scene background in composite mode:
 *
 * 1. Transmission (multiply): a child mesh sharing the geometry multiplies the
 *    framebuffer by the per-pixel transmittance T, so the skin or hair behind
 *    is tinted like through coloured glass. Beer–Lambert gives the optical
 *    path 1/cosθ inside the plate, lengthened on bevels and walls seen at an
 *    angle (thick edges darker and more saturated), Schlick's Fresnel removes
 *    the reflected share and steep walls lose part of the light to refraction
 *    (the dark outline of a glass edge). The framebuffer is sRGB-encoded, so T
 *    is written as T^(1/2.2) (a power law commutes with the product).
 * 2. Lit (add): the polished surface — clearcoat highlights, environment
 *    reflections, the body colour scattered back from inside and a faint glow —
 *    added on top. The blend runs on sRGB-encoded values, so the shader adds the
 *    exact increment `enc(dec(bg)·T + L) − bg·T^(1/2.2)` computed against the
 *    camera pixel: a dim body colour then stays a dim body colour instead of a
 *    milky veil (adding enc(L) would lift a 2 % reflection to +15 % on skin).
 *
 * A depth-only prepass (third child, opaque list) keeps both draws to the
 * front-most acetate surface: fused parts (end-piece cap inside the front,
 * temple butt behind the end piece) are not interfaces in a real frame and
 * must not stack their reflections; the far temple is hidden behind a
 * see-through rim (it stays visible through the lenses). A stencil bit per
 * draw keeps each pixel multiplied and added once even where the cap
 * triangulation overlaps itself by a hair (T-junctions), which an opaque
 * material hides but a multiply prints as a dark line.
 *
 * Opaque plates (transmission ≤ SEE_THROUGH_MIN, config/acetates) are a
 * single opaque physical material. Without a background (transparent export, legacy overlay) the lit
 * material falls back to normal alpha blending of the plate's apparent colour
 * and the multiply becomes a no-op, so the frame still reads solid.
 *
 * The plate photo is mapped triplanarly in object-space millimetres at its real
 * size, the frame centre (x = 0) mid-tile: a front is cut from one piece of
 * plate like a real frame, with no mirror line through the bridge.
 */

export const ACETATE_IOR = 1.49;
/** Extra optical path on bevels and walls seen at an angle (× (1 − cosθ)²). */
export const ACETATE_EDGE = 2;
/** Share of the light behind a steep wall or bevel bent away by refraction / internal reflection. */
export const ACETATE_REFRACTION_LOSS = 0.55;
/** Default clearcoat roughness: polished acetate, small sharp highlights. */
const COAT_ROUGHNESS = 0.07;

export type Rgb = [number, number, number];

export function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}
export function linearToSrgb(v: number): number {
  const c = Math.max(0, Math.min(1, v));
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}
export function hexToLinear(hex: string): Rgb {
  return [1, 3, 5].map(i => srgbToLinear(parseInt(hex.slice(i, i + 2), 16) / 255)) as Rgb;
}

/** Plain-number optics of one acetate, as the shaders receive them. */
export interface AcetateOptics {
  seeThrough: boolean;
  /** Flat transmittance at normal incidence (linear RGB): transmission × normalised tint. */
  tint: Rgb;
  /** Body colour (linear RGB): the plate's apparent colour. */
  body: Rgb;
  transmission: number;
  scatter: number;
  glow: number;
  roughness: number;
  coatRoughness: number;
  sheen: number;
  textureGain: number;
  textureGamma: number;
  textureAlbedo: number;
}

export function resolveAcetateOptics(spec: AcetateSpec): AcetateOptics {
  const body = hexToLinear(spec.color);
  // Default hue (the library sets explicit tints): a swatch seen over white
  // went through the plate twice.
  const hue = spec.tint ? hexToLinear(spec.tint) : body.map(Math.sqrt) as Rgb;
  const max = Math.max(...hue, 1e-4);
  const transmission = Math.max(0, Math.min(1, spec.transmission));
  return {
    seeThrough: isSeeThrough(spec),
    tint: hue.map(v => transmission * v / max) as Rgb,
    body,
    transmission,
    scatter: spec.scatter,
    glow: spec.glow ?? 0,
    roughness: spec.roughness,
    coatRoughness: spec.coatRoughness ?? COAT_ROUGHNESS,
    sheen: spec.sheen ?? 0,
    textureGain: spec.textureGain ?? 1,
    textureGamma: spec.textureGamma ?? 1,
    textureAlbedo: spec.textureAlbedo ?? 1,
  };
}

/**
 * Transmittance of one channel through the plate (mirrors `mataTransmittance`
 * in GLSL): Beer–Lambert over the refracted path and the Fresnel-transmitted
 * share, faded towards 1 (nothing drawn) with the frame.
 */
export function acetateTransmittance(t0: number, cosine: number, fade = 1, edge = ACETATE_EDGE, loss = ACETATE_REFRACTION_LOSS, thickness = 1, ior = ACETATE_IOR): number {
  const c = Math.max(1e-3, Math.min(1, Math.abs(cosine)));
  const cosInside = Math.sqrt(Math.max(1e-4, 1 - (1 - c * c) / (ior * ior)));
  const f0 = ((ior - 1) / (ior + 1)) ** 2;
  const fresnel = f0 + (1 - f0) * (1 - c) ** 5;
  const path = thickness * (1 + edge * (1 - c) ** 2) / cosInside;
  const s = Math.max(0, Math.min(1, (c - 0.6) / (0.12 - 0.6)));
  const bent = loss * s * s * (3 - 2 * s);
  const t = Math.max(1e-4, t0) ** path * (1 - fresnel) * (1 - bent);
  return 1 + (t - 1) * Math.max(0, Math.min(1, fade));
}

/** Plate UV of an object-space point (mm) before the mirrored wrap (mirrors GLSL). */
export function plateUv(a: number, b: number, plateMm: readonly [number, number]): [number, number] {
  return [(a + 0.5 * plateMm[0]) / plateMm[0], (b + 0.5 * plateMm[1]) / plateMm[1]];
}
/** GL MIRRORED_REPEAT applied to one coordinate. */
export function mirroredRepeat(u: number): number {
  const f = ((u % 2) + 2) % 2;
  return f <= 1 ? f : 2 - f;
}

interface SortItem { groupOrder: number; renderOrder: number; z: number; id: number; object: Object3D }
/**
 * Three's back-to-front transparent order, except that ties (same depth: a
 * transmission child shares its parent's world position) put each
 * transmission draw immediately before its own lit draw: on a surface the add
 * must follow the multiply. Everything else keeps three's order.
 */
export function acetateTransparentSort(a: SortItem, b: SortItem): number {
  if (a.groupOrder !== b.groupOrder) return a.groupOrder - b.groupOrder;
  if (a.renderOrder !== b.renderOrder) return a.renderOrder - b.renderOrder;
  if (a.z !== b.z) return b.z - a.z;
  const ka = (a.object.userData.mataSortId as number | undefined) ?? a.id;
  const kb = (b.object.userData.mataSortId as number | undefined) ?? b.id;
  if (ka !== kb) return ka - kb;
  const sa = (a.object.userData.mataSortSub as number | undefined) ?? 0;
  const sb = (b.object.userData.mataSortSub as number | undefined) ?? 0;
  return sa !== sb ? sa - sb : a.id - b.id;
}

// --- GLSL ------------------------------------------------------------------

const COMMON_GLSL = /* glsl */ `
uniform vec3 mataTint;
uniform vec3 mataBody;
uniform float mataTransmission;
uniform float mataIor;
uniform float mataThickness;
uniform float mataEdge;
uniform float mataRefraction;
uniform float mataFade;
uniform sampler2D mataPlate;
uniform float mataPlateReady;
uniform vec2 mataPlateMm;
uniform float mataPlateGain;
uniform float mataPlateGamma;
uniform float mataPlateAlbedo;
uniform vec3 mataOffset;
uniform float mataScale;
varying vec3 mataP;
varying vec3 mataN;

// Triplanar plate colour (linear RGB) in object-space millimetres, frame
// centre mid-tile; the texture wraps with mirrored repeat beyond the tile.
vec3 mataPlateSample() {
  vec3 p = (mataP + mataOffset) * mataScale;
  vec3 w = pow(abs(normalize(mataN)), vec3(6.0));
  w /= max(w.x + w.y + w.z, 1e-4);
  vec2 h = 0.5 * mataPlateMm;
  return texture2D(mataPlate, (p.zy + h) / mataPlateMm).rgb * w.x
       + texture2D(mataPlate, (p.xz + h) / mataPlateMm).rgb * w.y
       + texture2D(mataPlate, (p.xy + h) / mataPlateMm).rgb * w.z;
}

// Transmittance at normal incidence: flat tint, or the plate photo normalised
// so its clearest areas reach the plate's transmission (dark spots stay dense).
vec3 mataT0(vec3 plate) {
#ifdef MATA_PLATE
  vec3 t = mataTransmission * min(vec3(1.0), pow(max(plate * mataPlateGain, vec3(0.0)), vec3(mataPlateGamma)));
  return mix(mataTint, t, mataPlateReady);
#else
  return mataTint;
#endif
}

// Beer–Lambert along the refracted path + Fresnel-transmitted share, faded.
// Rounded bevels and walls seen at an angle cut through more acetate than the
// flat face (light runs along the rim): mataEdge lengthens the path there.
vec3 mataTransmittance(vec3 t0, vec3 n, vec3 v) {
  float c = clamp(abs(dot(n, v)), 1e-3, 1.0);
  float cosInside = sqrt(max(1.0 - (1.0 - c * c) / (mataIor * mataIor), 1e-4));
  float f0 = (mataIor - 1.0) / (mataIor + 1.0);
  f0 *= f0;
  float fresnel = f0 + (1.0 - f0) * pow(1.0 - c, 5.0);
  float path = mataThickness * (1.0 + mataEdge * (1.0 - c) * (1.0 - c)) / cosInside;
  // Steep walls and bevels bend the light from behind away (and reflect it
  // inside): the dark outline that draws a glass edge.
  float bent = mataRefraction * (1.0 - smoothstep(0.12, 0.6, c));
  vec3 t = pow(max(t0, vec3(1e-4)), vec3(path)) * (1.0 - fresnel) * (1.0 - bent);
  return mix(vec3(1.0), t, mataFade);
}
`;

const TRANSMISSION_VERTEX = /* glsl */ `
varying vec3 mataP;
varying vec3 mataN;
varying vec3 mataViewN;
varying vec3 mataViewPos;
#include <clipping_planes_pars_vertex>
void main() {
  mataP = position;
  mataN = normal;
  mataViewN = normalize(normalMatrix * normal);
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  mataViewPos = -mvPosition.xyz;
  gl_Position = projectionMatrix * mvPosition;
  #include <clipping_planes_vertex>
}
`;

const TRANSMISSION_FRAGMENT = /* glsl */ `
uniform float mataActive;
varying vec3 mataViewN;
varying vec3 mataViewPos;
${COMMON_GLSL}
#include <clipping_planes_pars_fragment>
void main() {
  // Material clipping planes (the temple cut at the ear, see TempleClip).
  #include <clipping_planes_fragment>
  if (mataActive < 0.5) { gl_FragColor = vec4(1.0); return; }
#ifdef MATA_PLATE
  vec3 plate = mataPlateSample();
#else
  vec3 plate = vec3(1.0);
#endif
  vec3 t = mataTransmittance(mataT0(plate), normalize(mataViewN), normalize(mataViewPos));
  // Multiplies the sRGB-encoded framebuffer: encode the transmittance alike.
  gl_FragColor = vec4(pow(t, vec3(1.0 / 2.2)), 1.0);
}
`;

const LIT_FRAGMENT_PARS = /* glsl */ `
${COMMON_GLSL}
uniform float mataComposite;
uniform float mataScatter;
uniform float mataGlow;
uniform sampler2D mataBg;
uniform mat3 mataBgUv;
uniform vec2 mataViewport;
uniform float mataBgMode;
uniform vec3 mataBgColor;
// Linear radiance of the camera pixel behind this fragment (the background).
vec3 mataBackground() {
  if (mataBgMode < 1.5) return mataBgColor;
  vec4 texel = texture2D(mataBg, (mataBgUv * vec3(gl_FragCoord.xy / mataViewport, 1.0)).xy);
  return mataBgMode < 2.5 ? sRGBTransferEOTF(texel).rgb : texel.rgb;
}
`;

const LIT_BODY = /* glsl */ `
#ifdef MATA_PLATE
  vec3 mataPlateLin = mataPlateSample();
  vec3 mataBodyColor = mix(mataBody, min(vec3(1.0), mataPlateLin * mataPlateAlbedo), mataPlateReady);
#else
  vec3 mataPlateLin = vec3(1.0);
  vec3 mataBodyColor = mataBody;
#endif
#ifdef MATA_SEE_THROUGH
  // Over the camera only the light scattered back from inside the plate is
  // body colour; without it (export) the apparent colour stands in.
  diffuseColor.rgb = mataBodyColor * mix(1.0, mataScatter, mataComposite);
  vec3 mataT0Value = mataT0(mataPlateLin);
#else
  diffuseColor.rgb = mataBodyColor;
#endif
`;

const LIT_EMISSIVE = /* glsl */ `
#include <emissivemap_fragment>
#ifdef MATA_SEE_THROUGH
  // Light caught inside the plate leaks out where the surface turns away
  // (bevels, walls): the glow is stronger there, like a thick rim's edge.
  float mataTurn = 1.0 - clamp(abs(dot(normal, normalize(vViewPosition))), 0.0, 1.0);
  totalEmissiveRadiance += mataBodyColor * mix(0.04, mataGlow * (0.6 + 1.2 * mataTurn), mataComposite);
#else
  totalEmissiveRadiance += mataBodyColor * mataGlow;
#endif
`;

const LIT_OUTPUT = /* glsl */ `
#ifdef MATA_SEE_THROUGH
  if (mataComposite > 0.5) {
    // Additive increment over camera×T (written by the transmission draw),
    // exact for the camera pixel behind: enc(dec(bg)·T + L) − enc(dec(bg))·T^(1/2.2).
    vec3 mataT = mataTransmittance(mataT0Value, normal, normalize(vViewPosition));
    vec3 mataBehind = mataBackground();
    vec3 mataTarget = sRGBTransferOETF(vec4(mataBehind * mataT + gl_FragColor.rgb * mataFade, 1.0)).rgb;
    vec3 mataBase = sRGBTransferOETF(vec4(mataBehind, 1.0)).rgb * pow(mataT, vec3(1.0 / 2.2));
    gl_FragColor = vec4(max(mataTarget - mataBase, vec3(0.0)), 1.0);
  } else {
    #include <colorspace_fragment>
  }
#else
  #include <colorspace_fragment>
#endif
`;

// --- Materials -------------------------------------------------------------

type Uniforms = Record<string, IUniform>;

/** Uniforms shared by every material of one dressing (one acetate on one frame). */
function sharedUniforms(o: AcetateOptics, plate: Texture | null, plateMm: readonly [number, number]): Uniforms {
  return {
    mataTint: { value: new Color(...o.tint) },
    mataBody: { value: new Color(...o.body) },
    mataTransmission: { value: o.transmission },
    mataIor: { value: ACETATE_IOR },
    mataThickness: { value: 1 },
    mataEdge: { value: ACETATE_EDGE },
    mataRefraction: { value: ACETATE_REFRACTION_LOSS },
    mataFade: { value: 1 },
    mataPlate: { value: plate },
    mataPlateReady: { value: plate?.image ? 1 : 0 },
    mataPlateMm: { value: new Vector2(plateMm[0], plateMm[1]) },
    mataPlateGain: { value: o.textureGain },
    mataPlateGamma: { value: o.textureGamma },
    mataPlateAlbedo: { value: o.textureAlbedo },
    mataComposite: { value: 0 },
    mataScatter: { value: o.scatter },
    mataGlow: { value: o.glow },
  };
}

const tmpScale = new Vector3();
const tmpSize = new Vector2();
/** World scale of the model (the frame root may be resized; hinges mirror X only). */
function modelScale(object: Object3D): number {
  return tmpScale.setFromMatrixColumn(object.matrixWorld, 1).length() || 1;
}

function compositeBackground(scene: Scene): Texture | Color | null {
  const bg = scene.background;
  if (bg instanceof Color) return bg;
  if (bg instanceof Texture && !(bg as Texture & { isCubeTexture?: boolean }).isCubeTexture) return bg;
  return null;
}

function createLitMaterial(o: AcetateOptics, shared: Uniforms, plate: Texture | null, offset: Vector3, legacyOpacity: number): MeshPhysicalMaterial {
  const mat = new MeshPhysicalMaterial({
    color: 0xffffff,
    roughness: o.roughness,
    metalness: 0,
    clearcoat: 1,
    clearcoatRoughness: o.coatRoughness,
    ior: ACETATE_IOR,
    // See-through: the clearcoat is the polished front surface; the base layer
    // stands for the back surface, seen through the body (tinted, dimmer).
    specularIntensity: o.seeThrough ? 0.2 : 0.5,
    specularColor: new Color(...o.tint.map(v => Math.min(1, 0.3 + 0.7 * v / Math.max(o.transmission, 0.05))) as Rgb),
    envMapIntensity: o.seeThrough ? 0.45 : 0.7,
    sheen: o.sheen * 0.6,
    sheenColor: new Color(...o.body).lerp(new Color(1, 1, 1), 0.6),
    sheenRoughness: 0.35,
    side: FrontSide,
    transparent: o.seeThrough,
    opacity: o.seeThrough ? legacyOpacity : 1,
    depthWrite: true,
  });
  if (!o.seeThrough) mat.specularColor.set(0xffffff);
  const local: Uniforms = {
    mataOffset: { value: offset.clone() },
    mataScale: { value: 1 },
    mataBg: { value: null },
    mataBgUv: { value: new Matrix3() },
    mataViewport: { value: new Vector2(1, 1) },
    mataBgMode: { value: 0 },
    mataBgColor: { value: new Color() },
  };
  mat.userData.mataUniforms = { ...shared, ...local };
  const defines = `${plate ? '#define MATA_PLATE\n' : ''}${o.seeThrough ? '#define MATA_SEE_THROUGH\n' : ''}`;
  mat.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, shared, local);
    shader.vertexShader = 'varying vec3 mataP;\nvarying vec3 mataN;\n' + shader.vertexShader.replace(
      '#include <begin_vertex>', '#include <begin_vertex>\nmataP = position;\nmataN = normal;');
    shader.fragmentShader = defines + LIT_FRAGMENT_PARS + shader.fragmentShader
      .replace('#include <map_fragment>', LIT_BODY)
      .replace('#include <emissivemap_fragment>', LIT_EMISSIVE)
      .replace('#include <colorspace_fragment>', LIT_OUTPUT);
  };
  // One program per optics family (plate / flat × see-through / solid), shared by all acetates.
  mat.customProgramCacheKey = () => `mata-acetate-A110V-${plate ? 'plate' : 'flat'}-${o.seeThrough ? 'glass' : 'solid'}`;
  mat.userData.baseOpacity = mat.opacity;
  mat.userData.baseTransparent = mat.transparent;
  if (o.seeThrough) {
    oncePerPixel(mat, 2);
    // Composite: add the lit increment. Alpha untouched (the camera stays opaque).
    mat.blendEquation = AddEquation;
    mat.blendSrc = SrcAlphaFactor;
    mat.blendDst = OneFactor;
    mat.blendEquationAlpha = AddEquation;
    mat.blendSrcAlpha = ZeroFactor;
    mat.blendDstAlpha = OneFactor;
    mat.userData.onFade = (fade: number) => {
      shared.mataFade!.value = fade;
      mat.opacity = legacyOpacity * fade;
    };
  }
  mat.onBeforeRender = (renderer: WebGLRenderer, scene: Scene, _camera: unknown, _geometry: unknown, object: Object3D) => {
    local.mataScale!.value = modelScale(object);
    if (!o.seeThrough) return;
    const bg = compositeBackground(scene);
    // Always `transparent`, so switching the blending keeps the same program.
    mat.blending = bg ? CustomBlending : NormalBlending;
    shared.mataComposite!.value = bg ? 1 : 0;
    if (bg instanceof Color) {
      local.mataBgMode!.value = 1;
      (local.mataBgColor!.value as Color).copy(bg);
    } else if (bg) {
      if (bg.matrixAutoUpdate) bg.updateMatrix();
      local.mataBg!.value = bg;
      (local.mataBgUv!.value as Matrix3).copy(bg.matrix);
      // Video frames are sampled as stored (sRGB bytes); sRGB canvases are decoded by the GPU.
      local.mataBgMode!.value = (bg as Texture & { isVideoTexture?: boolean }).isVideoTexture && bg.colorSpace === SRGBColorSpace ? 2 : 3;
      renderer.getDrawingBufferSize(tmpSize);
      (local.mataViewport!.value as Vector2).copy(tmpSize);
    }
  };
  return mat;
}

function createTransmissionMaterial(shared: Uniforms, plate: Texture | null, offset: Vector3): ShaderMaterial {
  const local: Uniforms = { mataActive: { value: 1 }, mataOffset: { value: offset.clone() }, mataScale: { value: 1 } };
  const mat = new ShaderMaterial({
    name: 'mata-acetate-transmission',
    uniforms: { ...shared, ...local },
    vertexShader: TRANSMISSION_VERTEX,
    fragmentShader: TRANSMISSION_FRAGMENT,
    defines: plate ? { MATA_PLATE: '' } : {},
    // Honour material clipping planes like the lit and depth draws of the same surface.
    clipping: true,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: FrontSide,
    blending: CustomBlending,
    blendEquation: AddEquation,
    blendSrc: ZeroFactor,
    blendDst: SrcColorFactor,
    blendEquationAlpha: AddEquation,
    blendSrcAlpha: ZeroFactor,
    blendDstAlpha: OneFactor,
  });
  oncePerPixel(mat, 1);
  mat.userData.mataUniforms = mat.uniforms;
  mat.userData.onFade = (fade: number) => { shared.mataFade!.value = fade; };
  mat.onBeforeRender = (_renderer: WebGLRenderer, scene: Scene, _camera: unknown, _geometry: unknown, object: Object3D) => {
    local.mataActive!.value = compositeBackground(scene) ? 1 : 0;
    local.mataScale!.value = modelScale(object);
    mat.uniformsNeedUpdate = true;
  };
  return mat;
}

/** Each pixel once per frame per draw kind: stencil bit `bit` (1 = transmission, 2 = lit). */
function oncePerPixel(mat: Material, bit: number): void {
  mat.stencilWrite = true;
  mat.stencilRef = bit;
  mat.stencilFuncMask = bit;
  mat.stencilWriteMask = bit;
  mat.stencilFunc = NotEqualStencilFunc;
  mat.stencilZPass = ReplaceStencilOp;
}

/** Depth-only prepass, pushed back a hair so the two colour draws pass on the same surface. */
function createDepthPrepassMaterial(): MeshBasicMaterial {
  const mat = new MeshBasicMaterial({ colorWrite: false, depthWrite: true, side: FrontSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
  mat.name = 'mata-acetate-depth';
  // Stays in the opaque list while the frame fades.
  mat.userData.onFade = () => {};
  return mat;
}

/**
 * Dresses the primary acetate meshes of a frame in one library acetate:
 * replaces their materials (the caller keeps the originals) and adds the
 * transmission and depth children. `dispose()` removes everything it added.
 */
/**
 * A110Y: the plate photos (≈ 1045×593, 3 MB on the GPU) are kept for the last few acetates seen,
 * so coming back to a plate neither decodes the JPEG nor uploads it again (each tap did both).
 * A plate still worn by a dressing is never disposed, however many others were seen since.
 */
interface PlateEntry { texture: Texture; loaded: boolean; users: number; waiters: Array<() => void> }
const plateCache = new Map<string, PlateEntry>();
const PLATE_CACHE_MAX = 4;

function acquirePlate(url: string): PlateEntry {
  let entry = plateCache.get(url);
  if (entry) {
    // Most recently used last.
    plateCache.delete(url);
    plateCache.set(url, entry);
  } else {
    const e: PlateEntry = { texture: null as unknown as Texture, loaded: false, users: 0, waiters: [] };
    e.texture = new TextureLoader().load(url, () => {
      e.loaded = true;
      for (const w of e.waiters) w();
      e.waiters.length = 0;
    });
    e.texture.colorSpace = SRGBColorSpace;
    e.texture.wrapS = e.texture.wrapT = MirroredRepeatWrapping;
    e.texture.anisotropy = 4;
    plateCache.set(url, e);
    entry = e;
  }
  entry.users++;
  // Evict the least recently used plates nobody wears.
  for (const [key, old] of plateCache) {
    if (plateCache.size <= PLATE_CACHE_MAX) break;
    if (old.users > 0 || old === entry) continue;
    plateCache.delete(key);
    old.texture.dispose();
  }
  return entry;
}

function releasePlate(url: string): void {
  const entry = plateCache.get(url);
  if (!entry) return;
  entry.users = Math.max(0, entry.users - 1);
  if (entry.users === 0 && plateCache.size > PLATE_CACHE_MAX) {
    plateCache.delete(url);
    entry.texture.dispose();
  }
}

/** Tests / teardown: forgets every cached plate. */
export function clearPlateCache(): void {
  for (const entry of plateCache.values()) entry.texture.dispose();
  plateCache.clear();
}

export class AcetateDressing {
  readonly optics: AcetateOptics;
  readonly materials: Material[] = [];
  /** Children added to the primary meshes (transmission + depth prepass). */
  readonly addedMeshes: Mesh[] = [];
  readonly plate: Texture | null;
  private readonly plateUrl: string | null;
  private readonly shared: Uniforms;

  constructor(model: Object3D, meshes: Mesh[], spec: AcetateSpec) {
    this.optics = resolveAcetateOptics(spec);
    const plateMm = spec.textureMm ?? [150, 85];
    let plate: Texture | null = null;
    let plateEntry: PlateEntry | null = null;
    if (spec.texture) {
      plateEntry = acquirePlate(spec.texture);
      plate = plateEntry.texture;
    }
    this.plate = plate;
    this.plateUrl = spec.texture ?? null;
    this.shared = sharedUniforms(this.optics, plate, plateMm);
    if (plateEntry) {
      const ready = (): void => { this.shared.mataPlateReady!.value = 1; };
      if (plateEntry.loaded) ready();
      else plateEntry.waiters.push(ready);
    }
    const legacy = legacyOpacity(spec);
    const depth = this.optics.seeThrough ? createDepthPrepassMaterial() : null;
    if (depth) this.materials.push(depth);
    let side = 0;
    for (const mesh of meshes) {
      // Front, rims and end pieces live in frame space (centre at x = 0); a
      // temple lives in its own space: centre it on the plate, each temple
      // cut from another band of the plate.
      const offset = new Vector3();
      if (mesh.parent === model) offset.copy(mesh.position);
      else {
        mesh.geometry.computeBoundingBox();
        mesh.geometry.boundingBox!.getCenter(offset).negate();
        offset.y += (side++ % 2 === 0 ? -0.22 : 0.22) * plateMm[1];
      }
      const lit = createLitMaterial(this.optics, this.shared, plate, offset, legacy);
      this.materials.push(lit);
      // A200 (from A198): fixed hardware slots (hybrid metal, nose pads) keep their own material;
      // collapsing the array would recolour the metal. Such meshes get no see-through extras.
      const slots = Array.isArray(mesh.material) ? mesh.material : null;
      const fixed = slots?.some(m => m.userData.fixedMetal) ?? false;
      mesh.material = fixed ? slots!.map(m => (m.userData.fixedMetal ? m : lit)) : lit;
      if (!this.optics.seeThrough || fixed) continue;
      const transmission = createTransmissionMaterial(this.shared, plate, offset);
      this.materials.push(transmission);
      const child = new Mesh(mesh.geometry, transmission);
      child.name = `${mesh.name}_transmission`;
      child.userData.acetateRole = 'transmission';
      // Drawn right before its parent's lit draw (see acetateTransparentSort).
      child.userData.mataSortId = mesh.id;
      child.userData.mataSortSub = -1;
      child.renderOrder = mesh.renderOrder;
      const prepass = new Mesh(mesh.geometry, depth!);
      prepass.name = `${mesh.name}_depth`;
      prepass.userData.acetateRole = 'depth';
      prepass.renderOrder = mesh.renderOrder;
      for (const added of [child, prepass]) {
        added.frustumCulled = false;
        added.castShadow = false;
        added.receiveShadow = false;
        mesh.add(added);
        this.addedMeshes.push(added);
      }
    }
  }

  /**
   * A110Y: removes the children it added, right away, while its materials stay alive (the
   * programs they hold are shared with the next acetate of the same family: see FrameAppearance).
   */
  detach(): void {
    for (const child of this.addedMeshes) child.removeFromParent();
    this.addedMeshes.length = 0;
  }

  dispose(): void {
    this.detach();
    for (const m of this.materials) m.dispose();
    this.materials.length = 0;
    // The plate stays in the cache for the next time this acetate is chosen (see acquirePlate).
    if (this.plateUrl) releasePlate(this.plateUrl);
  }
}
